<script setup>
import { computed } from 'vue'
import AppButton from '@/components/atoms/AppButton'
import AppSkeleton from '@/components/atoms/AppSkeleton'
import HelpHubChatIntro from './HelpHubChatIntro.vue'
import HelpHubChatRows from './HelpHubChatRows.vue'
import HelpHubChatSection from './HelpHubChatSection.vue'
import HelpHubChatThread from './HelpHubChatThread.vue'
import HelpHubState from '../HelpHubState.vue'
import { useSupportStore } from '@/stores/SupportStore'
import { useHelpHub } from '@/hooks/useHelpHub'
import { sectionSlice, splitConversations, ticketDigits } from '@/helpers/support'
import { HELP_HUB_CHAT_PAGE, HELP_HUB_TAB, SUPPORT_SECTION_LIMIT } from '@/constants'

/**
 * The Chat tab. Two views on one surface: the landing list of past conversations, tickets
 * and popular questions, and — once the dealer opens a row or says something — the thread.
 *
 * The thread is the one the URL names (`?help=ticket-32193`, which the panel resolves to a
 * conversation), or the one the store holds before it has a number; the composer is the
 * panel's footer.
 */
const supportStore = useSupportStore()
const { openHelp, openTicket, ticketNumber } = useHelpHub()

const split = computed(() => splitConversations(supportStore.conversations))
// Each section shows three rows; "View all" opens the list's own page.
const previous = computed(() => sectionSlice(split.value.previous))
const tickets = computed(() => sectionSlice(split.value.tickets))
const questions = computed(() => sectionSlice(supportStore.questions))

const hasMore = (list) => list.length > SUPPORT_SECTION_LIMIT
// The reply is outstanding while the send is in flight and the dealer's line is the last thing shown.
const awaiting = computed(() => supportStore.sending)
// Nothing to show yet, and nothing failed — the first load.
const loading = computed(() => supportStore.conversationsLoading && !supportStore.conversations.length)
// No history at all: three questions stand alone under the greeting, with no heading (Figma 28708:73192).
const bare = computed(() => !loading.value && !supportStore.conversations.length)

// The URL names a ticket (`?help=ticket-32193`) the loaded list has no conversation for: nothing to open, which the tab says instead of the landing.
const notFound = computed(() => !!ticketNumber.value && supportStore.conversationsLoaded && !supportStore.conversationsLoading
  && !supportStore.inConversation && !supportStore.conversations.some((conversation) => ticketDigits(conversation) === ticketNumber.value))
// The URL names a ticket the panel is still finding (the session, then the list): the thread's shape stands in, as for a thread that loads.
const resolving = computed(() => !!ticketNumber.value && !supportStore.inConversation && !notFound.value && !supportStore.conversationsFailed && !supportStore.failed)

supportStore.load()

// A row opens its thread through the URL, which names the ticket; a conversation the server gave no number opens in place.
const open = (conversation) => {
  const number = ticketDigits(conversation)
  return number ? openTicket(number) : supportStore.openConversation(conversation.id)
}
const ask = (question) => supportStore.send(question)
// "Try again" under the failed banner: a thread that never loaded is opened again; a send that failed refetches the thread.
const retry = () => (supportStore.messages.length ? supportStore.refreshMessages() : supportStore.openConversation(supportStore.activeId))
</script>

<template>
  <HelpHubChatThread
    v-if="supportStore.inConversation || resolving"
    :messages="supportStore.messages"
    :conversation="supportStore.activeConversation"
    :loading="supportStore.messagesLoading || resolving"
    :awaiting="awaiting"
    :progress="supportStore.progress"
    :failed="supportStore.failed"
    :escalating="supportStore.escalating"
    :escalated="supportStore.escalated"
    :show-resolution="supportStore.showResolution"
    :resolving="supportStore.resolving"
    @retry="retry"
    @choose="supportStore.send($event)"
    @escalate="supportStore.escalate()"
    @resolve="supportStore.resolve($event)"
    @dismiss-resolution="supportStore.dismissResolution()"
  />

  <!-- The URL names a ticket the dealer's list doesn't have: nothing to open, so say so (no frame draws this; the copy is ours). -->
  <HelpHubState
    v-else-if="notFound"
    icon="heroicons-outline:search"
    title="Conversation not found"
    description="This conversation may have been removed, or the link is wrong."
    action-label="Back to chat"
    action-icon="heroicons-outline:arrow-left"
    @action="openHelp(HELP_HUB_TAB.CHAT)"
  />

  <!-- Figma sets the mark 24px under the tabs; the tabs block already ends 12px under them. -->
  <div v-else class="pt-3">
    <HelpHubChatIntro />

    <!-- The rows' shape while the list loads; whose rows they will be is not known yet, so no heading. -->
    <HelpHubChatSection v-if="loading" :divided="false">
      <div role="status" class="flex flex-col gap-y-2">
        <span class="sr-only">Loading your conversations</span>
        <AppSkeleton v-for="row in 3" :key="row" height="h-[72px]" rounded="rounded-xl" />
      </div>
    </HelpHubChatSection>

    <HelpHubChatSection
      v-if="previous.length"
      title="Previous conversations"
      :divided="false"
      :view-all="hasMore(split.previous)"
      @view-all="openHelp(HELP_HUB_CHAT_PAGE.CONVERSATIONS)"
    >
      <HelpHubChatRows :list="HELP_HUB_CHAT_PAGE.CONVERSATIONS" :items="previous" compact @select="open" />
    </HelpHubChatSection>

    <!-- The support service answered, and the dealer has no open tickets: the section is simply absent. -->
    <HelpHubChatSection
      v-if="tickets.length"
      title="Support tickets"
      :divided="previous.length > 0"
      :view-all="hasMore(split.tickets)"
      @view-all="openHelp(HELP_HUB_CHAT_PAGE.TICKETS)"
    >
      <HelpHubChatRows :list="HELP_HUB_CHAT_PAGE.TICKETS" :items="tickets" compact @select="open" />
    </HelpHubChatSection>

    <!--
      Tapping a question ASKS it. The endpoint serves bare strings — no article, no id, no
      view count — so there is nothing to open, and sending it is what the support service's
      own widget does with these rows. With no history the three stand alone, with no heading
      and no "View all".
    -->
    <HelpHubChatSection
      v-if="questions.length"
      :title="bare ? '' : 'Popular questions'"
      :divided="!bare"
      :view-all="!bare && hasMore(supportStore.questions)"
      @view-all="openHelp(HELP_HUB_CHAT_PAGE.QUESTIONS)"
    >
      <HelpHubChatRows :list="HELP_HUB_CHAT_PAGE.QUESTIONS" :items="questions" compact @select="ask" />
    </HelpHubChatSection>

    <!-- Support is unreachable; the dealer still has the Help Center in the footer. -->
    <div v-if="supportStore.conversationsFailed" class="px-4 py-5">
      <div class="flex items-center justify-between gap-x-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
        <p class="text-xs text-gray-600">Couldn't reach support just now.</p>

        <AppButton variant="link" size="sm" @click="supportStore.load()">Retry</AppButton>
      </div>
    </div>
  </div>
</template>
